import useInvoices from '~/data/hooks/useInvoices';
import usePropertyContracts from '~/data/hooks/usePropertyContracts';
import { useTickets } from '~/data/hooks/useTickets';
import { InvoiceKind } from '~/data/schemas/invoice';
import { usePropertyCarsKpis } from '~/hooks/usePropertyCarsKpis';

/**
 * The numbers on the property page's tab labels.
 *
 * They ride on the tab strip, so they are fetched whether or not those tabs are open —
 * but two of the four cost nothing extra: the elevators and the contracts are the
 * requests those tabs themselves make, under the same SWR keys. Tickets and invoices are
 * single-row list requests read only for their totals.
 *
 * `undefined` while a count is in flight, which `tabLabelWithCount` renders as nothing.
 */
export function usePropertyRelatedCounts(propertyId: string | undefined) {
  const enabled = Boolean(propertyId);

  const { cars, loading: carsLoading } = usePropertyCarsKpis(propertyId ?? null);
  const { contracts, isLoading: contractsLoading } = usePropertyContracts(propertyId);
  const { numTickets, isLoading: ticketsLoading } = useTickets(
    { property: propertyId, limit: 1 },
    enabled,
  );
  // One read per class: the Spending tab gives each a subtab of its own, and a combined
  // total can't be split back into them here.
  const { metadata: invoiceMetadata, isLoading: invoicesLoading } = useInvoices(
    { property: propertyId, limit: '1', kind: [InvoiceKind.Invoice] },
    enabled,
  );
  const { metadata: proposalMetadata, isLoading: proposalsLoading } = useInvoices(
    { property: propertyId, limit: '1', kind: [InvoiceKind.Proposal] },
    enabled,
  );
  const { metadata: creditMetadata, isLoading: creditsLoading } = useInvoices(
    { property: propertyId, limit: '1', kind: [InvoiceKind.Credit] },
    enabled,
  );

  return {
    elevators: carsLoading ? undefined : (cars?.length ?? 0),
    contracts: contractsLoading ? undefined : contracts.length,
    tickets: ticketsLoading ? undefined : numTickets,
    invoices: invoicesLoading ? undefined : invoiceMetadata?.numInvoices,
    proposals: proposalsLoading ? undefined : proposalMetadata?.numInvoices,
    credits: creditsLoading ? undefined : creditMetadata?.numInvoices,
  };
}
